import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  acceptTeamInvite,
  ApiError,
  login as apiLogin,
  logout as apiLogout,
  setAuthToken,
} from "@/lib/api";
import { useMe } from "@/features/auth/useMe";
import { applyExplicitLogoutBoundary, unscopedKey } from "@/lib/queryClient";
import { Button } from "@/components/ui/Button";

/**
 * M9b E1–E7 — `/invite/{token}` two-flow landing.
 *
 * The token comes from the URL path. Two render branches:
 *   - Signed-in user: a one-button Accept that POSTs the token to
 *     `/api/team/invites/accept`. On success, route by
 *     `membership.role`.
 *   - Unauthenticated user: a create-account form (username,
 *     display_name, password). Submit POSTs the same accept
 *     endpoint with the new credentials; on success, automatically
 *     sign in via `/api/login` so the user lands authenticated.
 *     If auto-login fails, surface a "sign in manually" copy and
 *     route to `/login`.
 *
 * Post-accept routing by role (matches legacy `routeAfterInviteAcceptance`):
 *   - guardian / player → /feedback
 *   - team_admin       → /admin/people
 *   - coach / assistant_coach → /coach
 *   - anything else    → /  (defensive)
 *
 * Error states (E5) — backend returns three distinct statuses for
 * the unhappy paths and the UI gives the operator a useful
 * distinction:
 *   - 404 → "That invite is invalid or expired."
 *   - 409 → "This invite has already been used."
 *   - 410 → "This invite has expired."
 *
 * Privacy invariant (E6): the URL token never leaves the path →
 * mutation body. Never logged. The unauth create-account form
 * clears `password` on submit so the value doesn't linger in
 * React state after a failed attempt.
 */
export function InviteAcceptPage() {
  const params = useParams({ from: "/invite/$token" });
  const token = decodeURIComponent(params.token);
  const me = useMe();

  if (me.isLoading) {
    return (
      <main
        data-testid="invite-loading"
        className="min-h-screen flex items-center justify-center text-ink-muted"
      >
        Loading…
      </main>
    );
  }

  return (
    <main
      data-testid="invite-page"
      className="min-h-screen bg-canvas flex items-center justify-center p-4"
    >
      <section
        aria-label="Accept team invite"
        className="w-full max-w-md rounded border border-rule bg-surface p-6 flex flex-col gap-4"
      >
        <header>
          <h1 className="text-xl font-display tracking-tight text-ink">
            You&rsquo;re invited
          </h1>
          <p className="text-ink-muted text-xs">
            Create or use your account to join this team.
          </p>
        </header>

        {me.data?.user ? (
          <SignedInBranch
            token={token}
            username={me.data.user.username}
            userId={me.data.user.id}
          />
        ) : (
          <CreateAccountBranch token={token} />
        )}
      </section>
    </main>
  );
}

// ---------------------------------------------------------------------------
// E2 — Signed-in branch
// ---------------------------------------------------------------------------

function SignedInBranch({
  token,
  username,
  userId,
}: {
  token: string;
  username: string;
  userId: string;
}) {
  const navigate = useNavigate({ from: "/invite/$token" });
  const queryClient = useQueryClient();

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const accept = useMutation({
    // The backend's `accept_invite` service branches on whether
    // `user_id` is set: if it IS, it's the signed-in path (look up
    // the existing user); if it ISN'T, it falls into the
    // create-account path and 422s without username+password. So
    // the signed-in branch MUST send the actor's id in the body —
    // the backend route does not derive it from the Bearer token.
    // Reviewer-flagged on PR #19.
    mutationFn: () => acceptTeamInvite({ token, user_id: userId }),
    onSuccess: async (resp) => {
      setSuccess(true);
      // The user's memberships changed; refresh /api/me before
      // routing so the destination shell sees the new scope.
      await queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
      window.setTimeout(() => {
        void navigate({
          to: routeForRole(resp.membership.role),
          replace: true,
        });
      }, 600);
    },
    onError: (err) => {
      setError(formatInviteError(err));
    },
  });

  const switchAccount = async () => {
    try {
      await apiLogout();
    } catch {
      // ignore — server might already have revoked
    }
    applyExplicitLogoutBoundary();
    // Keep the invite flow anchored on the same URL after switching
    // accounts: the next signed-in user should come back here after
    // authenticating, not fall through to their default landing.
    void navigate({
      to: "/login",
      search: { next: `/invite/${encodeURIComponent(token)}` },
      replace: true,
    });
  };

  return (
    <div data-testid="invite-signed-in" className="flex flex-col gap-3">
      <p className="text-sm text-ink-muted">
        You&rsquo;re signed in as{" "}
        <span className="font-mono text-ink">@{username}</span>. Accepting
        will link this team to your account.
      </p>

      {success ? (
        <p
          data-testid="invite-accept-success"
          className="text-good text-xs font-mono"
        >
          Invite accepted. Taking you to your team…
        </p>
      ) : null}

      {error ? (
        <p
          data-testid="invite-accept-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}

      <div className="flex gap-2">
        <Button
          type="button"
          onClick={() => {
            setError(null);
            accept.mutate();
          }}
          disabled={accept.isPending || success}
          data-testid="invite-accept-btn"
        >
          {accept.isPending ? "Accepting…" : "Accept invite"}
        </Button>
        <button
          type="button"
          onClick={() => void switchAccount()}
          disabled={accept.isPending || success}
          data-testid="invite-switch-account"
          className="rounded border border-rule px-3 py-2 text-sm font-display uppercase tracking-wider text-ink-muted hover:text-ink hover:bg-field transition-colors disabled:opacity-50"
        >
          Sign in as someone else
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// E3 — Unauthenticated create-account branch
// ---------------------------------------------------------------------------

function CreateAccountBranch({ token }: { token: string }) {
  const navigate = useNavigate({ from: "/invite/$token" });
  const queryClient = useQueryClient();

  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSuccess(null);
    const u = username.trim();
    if (!u) {
      setError("Username is required.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    // Capture the password locally then clear React state before
    // the network calls — the on-screen field clears immediately
    // and the in-memory value lives only for the duration of the
    // two API calls below (E6 privacy invariant).
    const pw = password;
    setPassword("");
    try {
      const acceptResp = await acceptTeamInvite({
        token,
        username: u,
        password: pw,
        display_name: displayName.trim() || undefined,
      });
      setSuccess("Account created. Signing you in…");
      // Auto-login with the new credentials so the user lands
      // authenticated. If this fails for any reason, fall back to
      // the manual sign-in path (the account exists, the operator
      // just needs to authenticate explicitly).
      try {
        const loginResp = await apiLogin({ username: u, password: pw });
        setAuthToken(loginResp.token);
        await queryClient.invalidateQueries();
        window.setTimeout(() => {
          void navigate({
            to: routeForRole(acceptResp.membership.role),
            replace: true,
          });
        }, 600);
      } catch {
        setError(
          "Account created, but automatic sign-in failed. Sign in with your new account to continue.",
        );
        window.setTimeout(() => {
          void navigate({ to: "/login", replace: true });
        }, 1500);
      }
    } catch (err) {
      setError(formatInviteError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid="invite-create-account"
      onSubmit={handleSubmit}
      noValidate
      className="flex flex-col gap-3"
    >
      <p className="text-sm text-ink-muted">
        Create an account and join the team with this invite. Already have one?{" "}
        <button
          type="button"
          onClick={() => void navigate({ to: "/login" })}
          data-testid="invite-sign-in-instead"
          className="text-accent hover:underline"
        >
          Sign in instead
        </button>
        .
      </p>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Username
        </span>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
          required
          minLength={1}
          maxLength={100}
          autoComplete="username"
          data-testid="invite-create-username"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Display name
        </span>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={200}
          autoComplete="name"
          data-testid="invite-create-display-name"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <span className="text-[0.6rem] font-mono text-ink-muted">
          Optional. Visible to your teammates.
        </span>
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Choose a password
        </span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
          maxLength={200}
          autoComplete="new-password"
          data-testid="invite-create-password"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <span className="text-[0.6rem] font-mono text-ink-muted">
          At least 8 characters.
        </span>
      </label>

      {error ? (
        <p
          data-testid="invite-create-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}
      {success ? (
        <p
          data-testid="invite-create-success"
          className="text-good text-xs font-mono"
        >
          {success}
        </p>
      ) : null}

      <Button
        type="submit"
        disabled={submitting}
        data-testid="invite-create-submit"
        className="self-start"
      >
        {submitting ? "Creating…" : "Create account and join team"}
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// E4 — role → destination
// ---------------------------------------------------------------------------

function routeForRole(
  role: string,
): "/feedback" | "/admin/people" | "/coach" | "/" {
  const normalized = (role || "").toLowerCase();
  if (normalized === "guardian" || normalized === "player") return "/feedback";
  if (normalized === "team_admin") return "/admin/people";
  if (normalized === "coach" || normalized === "assistant_coach")
    return "/coach";
  return "/";
}

// ---------------------------------------------------------------------------
// E5 — error mapping
// ---------------------------------------------------------------------------

function formatInviteError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 404) return "That invite is invalid or expired.";
    if (err.status === 409) return "This invite has already been used.";
    if (err.status === 410) return "This invite has expired.";
    return typeof err.detail === "string"
      ? err.detail
      : `Could not accept the invite (HTTP ${err.status}).`;
  }
  return "Network error. Try again in a moment.";
}
